import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, map, of, switchMap, tap } from 'rxjs';
import { ExpenseService } from '../../services/expense.service';
import { NotificationService } from '../../services/notification.service';
import { ExpensesApiActions, ExpensesPageActions } from './expenses.actions';

const errorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error ? error.message : fallback;

@Injectable()
export class ExpensesEffects {
  private readonly actions$ = inject(Actions);
  private readonly expenseService = inject(ExpenseService);
  private readonly notificationService = inject(NotificationService);

  readonly loadExpenses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ExpensesPageActions.opened,
        ExpensesPageActions.filtersChanged,
        ExpensesPageActions.pageChanged,
      ),
      switchMap(() => this.expenseService.getAll().pipe(
        map((expenses) => ExpensesApiActions.loadSucceeded({ expenses })),
        catchError((error: { message?: string }) => of(
          ExpensesApiActions.loadFailed({ error: error?.message ?? 'Unable to load expenses' }),
        )),
      )),
    ),
  );

  readonly createExpense$ = createEffect(() => this.actions$.pipe(
    ofType(ExpensesPageActions.createRequested),
    concatMap(({ requestId, studentId, expense }) => this.expenseService.create(studentId, expense).pipe(
      map((created) => ExpensesApiActions.createSucceeded({ requestId, expense: created })),
      catchError((error: unknown) => of(ExpensesApiActions.createFailed({
        requestId,
        error: errorMessage(error, 'Unable to create expense'),
      }))),
    )),
  ));

  readonly updateExpense$ = createEffect(() => this.actions$.pipe(
    ofType(ExpensesPageActions.updateRequested),
    concatMap(({ requestId, studentId, expenseId, expense }) => 
      this.expenseService.update(studentId, expenseId, expense).pipe(
        map((updated) => ExpensesApiActions.updateSucceeded({ requestId, expense: updated })),
        catchError((error: unknown) => of(ExpensesApiActions.updateFailed({
          requestId,
          error: errorMessage(error, 'Unable to update expense'),
        }))),
      )),
  ));

  readonly approveExpense$ = createEffect(() => this.actions$.pipe(
    ofType(ExpensesPageActions.approveRequested),
    concatMap(({ requestId, studentId, expenseId }) => 
      this.expenseService.approve(studentId, expenseId).pipe(
        map((approved) => ExpensesApiActions.approveSucceeded({ requestId, expense: approved })),
        catchError((error: unknown) => of(ExpensesApiActions.approveFailed({
          requestId,
          error: errorMessage(error, 'Unable to approve expense'),
        }))),
      )),
  ));

  readonly rejectExpense$ = createEffect(() => this.actions$.pipe(
    ofType(ExpensesPageActions.rejectRequested),
    concatMap(({ requestId, studentId, expenseId, reason }) => 
      this.expenseService.reject(studentId, expenseId, reason).pipe(
        map((rejected) => ExpensesApiActions.rejectSucceeded({ requestId, expense: rejected })),
        catchError((error: unknown) => of(ExpensesApiActions.rejectFailed({
          requestId,
          error: errorMessage(error, 'Unable to reject expense'),
        }))),
      )),
  ));

  readonly closeExpense$ = createEffect(() => this.actions$.pipe(
    ofType(ExpensesPageActions.closeRequested),
    concatMap(({ requestId, studentId, expenseId, data }) => 
      this.expenseService.close(studentId, expenseId, data).pipe(
        map((closed) => ExpensesApiActions.closeSucceeded({ requestId, expense: closed })),
        catchError((error: unknown) => of(ExpensesApiActions.closeFailed({
          requestId,
          error: errorMessage(error, 'Unable to close expense'),
        }))),
      )),
  ));

  readonly deleteExpense$ = createEffect(() => this.actions$.pipe(
    ofType(ExpensesPageActions.deleteRequested),
    concatMap(({ requestId, studentId, expenseId }) => 
      this.expenseService.delete(studentId, expenseId).pipe(
        map(() => ExpensesApiActions.deleteSucceeded({ requestId, expenseId })),
        catchError((error: unknown) => of(ExpensesApiActions.deleteFailed({
          requestId,
          error: errorMessage(error, 'Unable to delete expense'),
        }))),
      )),
  ));

  readonly notifyLoadFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(ExpensesApiActions.loadFailed),
      tap(() => this.notificationService.error('EXPENSES.ERROR_LOADING')),
    ),
    { dispatch: false },
  );
}